/**
 * AUI Component Registry
 *
 * Maps component type strings from agent surface specs to React components.
 * Add custom components here to make them available to agents.
 */

import type { ReactNode } from 'react';
import type { AuiNode, AuiAction } from '../../types/canvases';
import type { ComponentType } from './schemas/component-types';
import { AuiColumn } from './components/AuiColumn';
import { AuiRow } from './components/AuiRow';
import { AuiText } from './components/AuiText';
import { AuiCard } from './components/AuiCard';
import { AuiButton } from './components/AuiButton';
import { AuiTable } from './components/AuiTable';
import { AuiBadge } from './components/AuiBadge';
import { AuiAlert } from './components/AuiAlert';
import { AuiTabs } from './components/AuiTabs';
import { AuiMarkdownEditor } from './components/AuiMarkdownEditor';
import {
  AuiStatCard,
  AuiStatsOverview,
  AuiChartCard,
  AuiAreaChart,
  AuiBarChart,
  AuiLineChart,
  AuiPieChart,
  AuiRadarChart,
  AuiScatterChart,
  AuiTreemapChart,
  AuiSparkline,
  AuiGeoMapChart,
} from './components/charts';
import { AuiGrid } from './components/AuiGrid';
import { AuiTaskDashboard } from './components/AuiTaskDashboard';
import { AuiAgentTaskList } from './components/AuiAgentTaskList';
import { AuiFiles } from './components/AuiFiles';
import { AuiCalendar } from './components/calendar';

export interface AuiRendererProps {
  node: AuiNode;
  children?: ReactNode;
  childNodes?: AuiNode[];
  onAction?: (action: AuiAction) => Promise<void>;
}

export type AuiRendererComponent = React.FC<AuiRendererProps>;

// `satisfies` enforces compile-time coverage: adding a name to
// `componentTypes` without a renderer here is a TypeScript error. The
// exported type is broadened to `Partial<Record<string, ...>>` so callers
// can look up by an arbitrary string and get back `T | undefined`.
const _registry = {
  Column: AuiColumn,
  Row: AuiRow,
  Text: AuiText,
  Card: AuiCard,
  Button: AuiButton,
  Table: AuiTable,
  Badge: AuiBadge,
  Alert: AuiAlert,
  Tabs: AuiTabs,
  MarkdownEditor: AuiMarkdownEditor,
  StatCard: AuiStatCard,
  StatsOverview: AuiStatsOverview,
  ChartCard: AuiChartCard,
  AreaChart: AuiAreaChart,
  BarChart: AuiBarChart,
  LineChart: AuiLineChart,
  PieChart: AuiPieChart,
  RadarChart: AuiRadarChart,
  ScatterChart: AuiScatterChart,
  TreemapChart: AuiTreemapChart,
  Sparkline: AuiSparkline,
  GeoMapChart: AuiGeoMapChart,
  Grid: AuiGrid,
  TaskDashboard: AuiTaskDashboard,
  AgentTaskList: AuiAgentTaskList,
  Files: AuiFiles,
  Calendar: AuiCalendar,
} satisfies Record<ComponentType, AuiRendererComponent>;

export const componentRegistry: Partial<Record<string, AuiRendererComponent>> = _registry;
